import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ContributionFormData } from '../../../types/client';
import { clientMasterApi } from '../../../services/api';

interface UseContributionsProps {
    clientId?: number;
    clientCode: string;
    schemeId: number;
    viewMode?: boolean;
    onModified?: () => void;
    data?: ContributionFormData[];
    previousContributions?: ContributionFormData[];
}

/**
 * A contribution as the table edits it. Top up and redemption are entered per row; the
 * cumulative `amount` / `redemption_amount` the API stores are rebuilt from them, so a row
 * can be added, re-dated or removed anywhere in the series. `valid_to` is derived by the
 * backend and never edited here.
 */
export interface ContributionRow extends ContributionFormData {
    key: string;               // Stable React key: rows re-sort when a date changes.
    redemption_delta: number;  // This row's own redemption.
}

const todayStr = () => new Date().toISOString().split('T')[0];

// Money to 2 decimals, so float sums/differences never surface as 0.19999999999999998.
const round2 = (n: number) => Math.round(n * 100) / 100;

const byValidFromDesc = (a: ContributionFormData, b: ContributionFormData) =>
    (b.valid_from || '').localeCompare(a.valid_from || '');

const normalize = (c: any): ContributionFormData => ({
    ...c,
    valid_from: c.valid_from || todayStr(),
    amount: Number(c.amount) || 0,
    top_up_amount: Number(c.top_up_amount) || 0,
    redemption_amount: Number(c.redemption_amount) || 0,
});

/** Newest first, rebuilding both cumulatives as running sums from the oldest row up. */
const withCumulatives = (rows: ContributionRow[]): ContributionRow[] => {
    const sorted = [...rows].sort(byValidFromDesc);
    let amount = 0;
    let redemption = 0;
    for (let i = sorted.length - 1; i >= 0; i--) {
        amount = round2(amount + Number(sorted[i].top_up_amount || 0));
        redemption = round2(redemption + Number(sorted[i].redemption_delta || 0));
        sorted[i] = { ...sorted[i], amount, redemption_amount: redemption };
    }
    return sorted;
};

const isContributionSetChanged = (
    current: ContributionFormData[],
    previous: ContributionFormData[],
    deletedIds: number[] = [],
): boolean => {
    if (deletedIds.length > 0) return true;
    if (!previous || previous.length === 0) {
        return current.length > 0;
    }
    if (current.length !== previous.length) return true;
    for (let i = 0; i < current.length; i++) {
        const c = current[i];
        const p = previous[i];
        if (!p) return true;
        if (
            (c.valid_from || '') !== (p.valid_from || '') ||
            Number(c.amount) !== Number(p.amount) ||
            Number(c.top_up_amount) !== Number(p.top_up_amount) ||
            Number(c.redemption_amount) !== Number(p.redemption_amount)
        ) {
            return true;
        }
    }
    return false;
};

export const useContributions = ({
    clientId,
    clientCode,
    schemeId,
    viewMode,
    onModified,
    data,
    previousContributions = []
}: UseContributionsProps) => {
    const [rows, setRows] = useState<ContributionRow[]>([]);
    // Starts true when the mount effect will fetch, so the empty state never flashes first.
    const [loading, setLoading] = useState(() => !data && !!clientId);
    const [errors, setErrors] = useState<any[]>([]);
    const [deletedIds, setDeletedIds] = useState<number[]>([]);
    const nextKey = useRef(0);
    // Set by a date edit, so a blur with no edit leaves the stored totals alone.
    const datesEdited = useRef(false);

    // Loaded rows keep their stored cumulatives untouched; each row's own redemption is
    // read back off them. Cumulatives are only rebuilt once something is edited.
    const toRows = useCallback((list: any[]): ContributionRow[] => {
        const sorted = list.map(normalize).sort(byValidFromDesc);
        return sorted.map((c, i) => ({
            ...c,
            key: c.id != null ? `id-${c.id}` : `new-${nextKey.current++}`,
            redemption_delta: round2(Number(c.redemption_amount || 0) - Number(sorted[i + 1]?.redemption_amount || 0)),
        }));
    }, []);

    const refreshData = useCallback(async (signal?: AbortSignal) => {
        if (data) {
            const loaded = toRows(data);
            setRows(loaded);
            setErrors(new Array(loaded.length).fill({}));
            setDeletedIds([]);
            setLoading(false);
            return;
        }
        if (!clientId) return;
        setLoading(true);
        try {
            const response = await clientMasterApi.getContributions(clientId, { signal });
            const list = Array.isArray(response) ? response : ((response as any).results || [response]);
            const loaded = toRows(list);
            setRows(loaded);
            setErrors(new Array(loaded.length).fill({}));
            setDeletedIds([]);
        } catch (e: any) {
            if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
                console.error(e);
            }
        } finally {
            // Only turn off loading if not aborted (optional, but cleaner)
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [clientId, data, toRows]);

    useEffect(() => {
        const controller = new AbortController();
        refreshData(controller.signal);
        return () => controller.abort();
    }, [refreshData]);

    const isChanged = useMemo(
        () => isContributionSetChanged(
            rows,
            previousContributions.map(normalize).sort(byValidFromDesc),
            deletedIds
        ),
        [rows, previousContributions, deletedIds]
    );

    const commitRows = (next: ContributionRow[]) => {
        setRows(withCumulatives(next));
        setErrors([]);
        onModified?.();
    };

    const handleAdd = () => {
        if (viewMode) return;
        commitRows([
            {
                key: `new-${nextKey.current++}`,
                client_code: clientCode,
                scheme: schemeId,
                valid_from: todayStr(),
                amount: 0,
                top_up_amount: 0,
                redemption_amount: 0,
                redemption_delta: 0,
                is_active: true,
            },
            ...rows,
        ]);
    };

    const handleChange = (
        index: number,
        field: 'valid_from' | 'top_up_amount' | 'redemption_delta',
        rawValue: any
    ) => {
        if (viewMode) return;
        const next = [...rows];
        if (field === 'valid_from') {
            // Kept in place while typing (each keystroke can be a valid date); the row moves
            // to its sorted position on blur, via commitDates.
            next[index] = { ...next[index], valid_from: rawValue };
            datesEdited.current = true;
            setRows(next);
            onModified?.();
            return;
        }
        next[index] = { ...next[index], [field]: round2(Math.max(0, Number(rawValue) || 0)) };
        commitRows(next);
    };

    const commitDates = () => {
        if (viewMode || !datesEdited.current) return;
        datesEdited.current = false;
        commitRows(rows);
    };

    const handleRemove = (index: number) => {
        if (viewMode) return;
        const id = rows[index].id;
        if (id) {
            setDeletedIds(prev => [...prev, id]);
        }
        commitRows(rows.filter((_, i) => i !== index));
    };

    const validate = () => {
        let ok = true;
        const nextErrors = rows.map((r, i) => {
            const e: any = {};
            const prev = rows[i + 1]; // The row before this one in time (rows are newest first).

            if (!r.valid_from) {
                e.valid_from = 'Required';
                ok = false;
            } else if (rows.some((other, j) => j !== i && other.valid_from === r.valid_from)) {
                e.valid_from = 'Duplicate date';
                ok = false;
            }

            if (r.redemption_delta < 0) {
                e.redemption_amount = 'Redemption cannot be negative';
                ok = false;
            } else if (!prev && r.redemption_delta > 0) {
                e.redemption_amount = 'Cannot redeem first contribution';
                ok = false;
            } else if (prev && Number(r.redemption_amount) > Number(prev.amount)) {
                e.redemption_amount = 'Cumulative redemption exceeds the previous cumulative top up';
                ok = false;
            }
            return e;
        });
        setErrors(nextErrors);
        return ok;
    };

    return {
        rows,
        loading,
        errors,
        deletedIds,
        isChanged,
        handleAdd,
        handleChange,
        commitDates,
        handleRemove,
        validate,
        refresh: refreshData
    };
};
